
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { NodeData, NodeType } from '../../types';
import { getTextModels, AI_MODELS } from '../../models/config';
import { FileText, Upload, Loader2, Play, AlertCircle, CheckCircle2, Trash2, ChevronLeft, ChevronRight, DownloadCloud, Cpu, Download, Sparkles } from 'lucide-react';
import { NodeKindIcon, nodeTone } from './nodeTheme';
import { SettingChip } from './composer/Composer';
import { PROVIDER_NAMES, shortModelLabel } from './composer/modelLabels';
import { GoogleGenAI } from "@google/genai";
import { loadLocalModel, generateLocal, isLocalModelLoaded } from '../../utils/webLlmService';
import type { InitProgressReport } from '@mlc-ai/web-llm';

/**
 * The PDF reader, loaded the first time a script is read rather than with the app, with its
 * worker from this site: the same version as the library, and allowed by the site's CSP.
 */
let pdfjsReady: Promise<typeof import('pdfjs-dist')> | null = null;
const loadPdfjs = () => {
  pdfjsReady ??= Promise.all([import('pdfjs-dist'), import('pdfjs-dist/build/pdf.worker.min.mjs?url')])
    .then(([lib, worker]) => {
      lib.GlobalWorkerOptions.workerSrc = worker.default;
      return lib;
    })
    .catch((error) => { pdfjsReady = null; throw error; });
  return pdfjsReady;
};

interface ScriptNodeProps {
  data: NodeData;
  onDataChange: (newData: NodeData) => void;
  apiKey?: string;
  onSpawnNodes: (nodesData: any[], type?: NodeType) => void;
  onResize: (id: string, size: { width: number; height: number }) => void;
  nodeId: string;
  isViewOnly?: boolean;
}

interface ScriptPage {
  text: string;
  pageIndex: number;
}

export const ScriptNode: React.FC<ScriptNodeProps> = ({
  data,
  onDataChange,
  apiKey,
  onSpawnNodes,
  onResize,
  nodeId,
  isViewOnly = false,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [showKeyError, setShowKeyError] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  
  // Local LLM State
  const [downloadProgress, setDownloadProgress] = useState<string>('');
  const [isModelDownloading, setIsModelDownloading] = useState(false);
  const [localModelReady, setLocalModelReady] = useState(false);
  
  // Script Content State
  const [pages, setPages] = useState<ScriptPage[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  
  // View State
  const [viewState, setViewState] = useState({ scale: 1, x: 0, y: 0 });

  // References
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Standard US Letter dimensions (Points)
  const PAGE_WIDTH = 612;
  const PAGE_HEIGHT = 792;
  const FALLBACK_CHAR_WIDTH = 7.2; 
  const LINE_HEIGHT = 12; 

  // Default to 2.5 Flash
  const savedModelId = data.scriptConfig?.model;
  const currentModelId = (savedModelId && AI_MODELS[savedModelId]) 
      ? savedModelId 
      : 'gemini-2.5-flash';

  const modelDef = AI_MODELS[currentModelId];
  const isLocalModel = modelDef?.provider === 'webllm';

  const fileName = data.scriptConfig?.fileName;
  const hasFile = !!data.scriptConfig?.fileData;
  const status = data.generationStatus || 'idle';

  const textModels = useMemo(() => getTextModels(), []);
  // The model chip's list: the cloud's models under their provider, those that run in the browser under "On this computer"
  const modelOptions = useMemo(
      () => textModels.map(m => ({ value: m.id, label: shortModelLabel(m.label), group: m.provider === 'webllm' ? 'On this computer' : PROVIDER_NAMES[m.provider] ?? m.provider })),
      [textModels],
  );

  const updateConfig = (updates: Partial<typeof data.scriptConfig>) => {
      if (isViewOnly) return;
      onDataChange({
          ...data,
          scriptConfig: {
              ...data.scriptConfig,
              model: currentModelId,
              ...updates
          }
      });
  };

  // Check Local Model Status
  useEffect(() => {
      if (isLocalModel) {
          const checkReady = () => setLocalModelReady(isLocalModelLoaded(currentModelId));
          checkReady();
          // Poll briefly or check on focus in case another node loaded it
          const interval = setInterval(checkReady, 1000);
          return () => clearInterval(interval);
      } else {
          setLocalModelReady(true);
      }
  }, [currentModelId, isLocalModel]);

  // --- AUTO SCALING & CENTERING ---
  useEffect(() => {
    if (!containerRef.current || pages.length === 0) return;

    const updateLayout = () => {
        if (!containerRef.current) return;
        
        const width = containerRef.current.offsetWidth;
        const height = containerRef.current.offsetHeight;
        
        const padding = 20;
        const availableW = Math.max(0, width - padding);
        const availableH = Math.max(0, height - padding);

        // Calculate Scale to Fit
        const scaleX = availableW / PAGE_WIDTH;
        const scaleY = availableH / PAGE_HEIGHT;
        const newScale = Math.min(scaleX, scaleY);
        
        // Calculate Centering Offsets
        const scaledWidth = PAGE_WIDTH * newScale;
        const scaledHeight = PAGE_HEIGHT * newScale;
        
        const x = (width - scaledWidth) / 2;
        const y = (height - scaledHeight) / 2;

        setViewState({ scale: newScale, x, y });
    };

    const resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(updateLayout);
    });

    resizeObserver.observe(containerRef.current);
    updateLayout(); // Initial call

    return () => resizeObserver.disconnect();
  }, [pages.length]);


  // --- TEXT EXTRACTION ---
  useEffect(() => {
    const extractTextFromPdf = async () => {
        if (!data.scriptConfig?.fileData) {
            setPages([]);
            return;
        }
        
        setIsExtracting(true);
        try {
            const binaryString = window.atob(data.scriptConfig.fileData);
            const len = binaryString.length;
            const bytes = new Uint8Array(len);
            for (let i = 0; i < len; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }

            const pdfjsLib = await loadPdfjs();
            const loadingTask = pdfjsLib.getDocument({ data: bytes });
            const doc = await loadingTask.promise;
            
            let extractedPages: ScriptPage[] = [];

            for (let i = 1; i <= doc.numPages; i++) {
                const page = await doc.getPage(i);
                const textContent = await page.getTextContent();
                const viewport = page.getViewport({ scale: 1.0 });

                // 1. Extract raw items with flipped Y
                const items = textContent.items.map((item: any) => {
                    const tx = item.transform;
                    return {
                        text: item.str,
                        x: tx[4],
                        y: viewport.height - tx[5], // Flip Y so 0 is top
                        width: item.width,
                        height: item.height
                    };
                });

                // 2. Filter empty items to avoid noise
                const validItems = items.filter(it => it.text.trim().length > 0);
                
                if (validItems.length === 0) {
                     extractedPages.push({ pageIndex: i, text: "" });
                     continue;
                }

                // 3. Detect Metrics (Median Char Width & Left Margin)
                const charWidths = validItems
                    .map(it => it.text.length > 0 ? it.width / it.text.length : 0)
                    .filter(w => w > 0);
                
                charWidths.sort((a, b) => a - b);
                const medianCharWidth = charWidths.length > 0 
                    ? charWidths[Math.floor(charWidths.length / 2)] 
                    : FALLBACK_CHAR_WIDTH;

                // Find global min X for this page (margin)
                let minX = validItems.reduce((min, it) => Math.min(min, it.x), Number.MAX_VALUE);
                if (minX === Number.MAX_VALUE) minX = 0;

                // 4. Group items into logical lines (y-axis clustering)
                const lineMap = new Map<number, any[]>();
                // Sort by Y first to help grouping
                items.sort((a, b) => a.y - b.y);

                for (const item of items) {
                     let foundY = -1;
                     // Check existing lines for tolerance
                     for (const key of lineMap.keys()) {
                         if (Math.abs(key - item.y) < 6) { 
                             foundY = key;
                             break;
                         }
                     }
                     if (foundY !== -1) {
                         lineMap.get(foundY)?.push(item);
                     } else {
                         lineMap.set(item.y, [item]);
                     }
                }

                // 5. Construct Text
                const sortedY = Array.from(lineMap.keys()).sort((a, b) => a - b);
                
                let pageText = "";
                let lastY = sortedY.length > 0 ? sortedY[0] : 0;

                const constructLine = (lineItems: any[]) => {
                    // Sort by X position
                    lineItems.sort((a: any, b: any) => a.x - b.x);
                    
                    let lineStr = "";
                    // Start relative to the calculated margin
                    let currentX = minX; 
                    
                    for (const item of lineItems) {
                        // Calculate gap from current cursor position
                        const gap = item.x - currentX;
                        
                        if (currentX === minX) {
                            // Start of line: Indentation (Alignment)
                            if (gap > medianCharWidth * 0.5) {
                                const spaces = Math.round(gap / medianCharWidth);
                                lineStr += " ".repeat(spaces);
                            }
                        } else {
                            // Middle of line: Word Spacing
                            if (gap > medianCharWidth * 0.3) {
                                let spaces = Math.round(gap / medianCharWidth);
                                if (gap < medianCharWidth * 1.8) {
                                    spaces = 1;
                                }
                                lineStr += " ".repeat(Math.max(1, spaces));
                            }
                        }
                        
                        lineStr += item.text;
                        currentX = item.x + item.width;
                    }
                    return lineStr;
                };

                for (const y of sortedY) {
                    const lineItems = lineMap.get(y)!;
                    const lineStr = constructLine(lineItems);
                    
                    if (pageText.length > 0) { 
                        const diff = y - lastY;
                        const newlines = Math.max(1, Math.round(diff / LINE_HEIGHT));
                        pageText += '\n'.repeat(newlines);
                    }
                    
                    pageText += lineStr;
                    lastY = y;
                }

                extractedPages.push({
                    pageIndex: i,
                    text: pageText
                });
            }

            setPages(extractedPages);
            setCurrentPage(1);

            // Set initial node size
            onResize(nodeId, { width: 400, height: 520 + 100 }); 

        } catch (error) {
            console.error("Error extracting PDF:", error);
            onDataChange({ ...data, errorMessage: "Failed to read PDF text" });
        } finally {
            setIsExtracting(false);
        }
    };

    if (hasFile) {
        extractTextFromPdf();
    }
  }, [data.scriptConfig?.fileData, hasFile, nodeId, onResize]);

  // Helper to separate indent for rendering
  const parseLine = (rawLine: string) => {
      // Find leading spaces
      const match = rawLine.match(/^(\s*)(.*)/);
      const indent = match ? match[1] : '';
      const text = match ? match[2] : '';
      return { indent, text };
  };

  const currentLines = useMemo(() => {
      return (pages[currentPage - 1]?.text || "").split('\n');
  }, [pages, currentPage]);

  const handleDragStart = (e: React.DragEvent) => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      const container = containerRef.current;
      if (!container) return;

      const lines = container.querySelectorAll('.script-line');
      let finalPayload = '';

      lines.forEach((line) => {
          if (selection.containsNode(line, true)) {
              const indent = line.getAttribute('data-indent') || '';
              const textEl = line.querySelector('.text-content');
              if (!textEl) return;
              
              const fullText = textEl.textContent || '';
              let outputText = fullText;
              let useIndent = true;

              if (range.startContainer === textEl.firstChild || range.startContainer === textEl) {
                   if (range.startOffset > 0) {
                       outputText = fullText.substring(range.startOffset);
                       useIndent = false;
                   }
              }

              if (range.endContainer === textEl.firstChild || range.endContainer === textEl) {
                   if (range.startContainer === range.endContainer) {
                       outputText = fullText.substring(range.startOffset, range.endOffset);
                       if (range.startOffset > 0) useIndent = false;
                   } else {
                       outputText = outputText.substring(0, range.endOffset);
                   }
              }

              if (outputText.trim() === '' && fullText === '') {
                   finalPayload += '\n';
              } else {
                   finalPayload += (useIndent ? indent : '') + outputText + '\n';
              }
          }
      });

      if (!finalPayload) {
          finalPayload = selection.toString();
      }

      e.dataTransfer.setData('text/plain', finalPayload.replace(/\n$/, ''));
      e.dataTransfer.effectAllowed = 'copy';
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isViewOnly) return;
      const file = e.target.files?.[0];
      if (file) processFile(file);
  };

  const processFile = (file: File) => {
      if (isViewOnly) return;
      if (file.type !== 'application/pdf') {
          onDataChange({ ...data, errorMessage: 'Only PDF files are supported.' });
          return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
          const result = e.target?.result as string;
          const base64 = result.split(',')[1];
          
          onDataChange({
              ...data,
              errorMessage: undefined,
              scriptConfig: {
                  ...data.scriptConfig,
                  model: currentModelId,
                  fileName: file.name,
                  fileData: base64,
                  mimeType: file.type
              }
          });
      };
      reader.readAsDataURL(file);
  };

  const getFullText = () => pages.map(p => p.text).join('\n\n');

  // --- LOCAL LLM HANDLER ---

  const handleLoadModel = async () => {
      if (!isLocalModel) return;
      setIsModelDownloading(true);
      setDownloadProgress('Initializing...');
      try {
          await loadLocalModel(currentModelId, (report: InitProgressReport) => {
              setDownloadProgress(report.text);
          });
          setLocalModelReady(true);
      } catch (e: any) {
          console.error(e);
          // Show alert immediately to user
          alert("Failed to load local model:\n" + e.message);
          onDataChange({ ...data, generationStatus: 'error', errorMessage: "Load Failed: " + e.message });
      } finally {
          setIsModelDownloading(false);
      }
  };

  const handleLocalBreakdown = async () => {
      setIsProcessing(true);
      onDataChange({ ...data, generationStatus: 'generating', errorMessage: undefined });

      try {
          // Double check loaded state
          if (!isLocalModelLoaded(currentModelId)) {
             await handleLoadModel();
             if (!isLocalModelLoaded(currentModelId)) {
                 throw new Error("Model failed to load. Please check your connection or GPU support.");
             }
          }

          const fullScriptText = getFullText();
          const systemPrompt = `
            You are an expert film director. Analyze the screenplay provided by the user.
            
            Format Output as a strictly valid JSON array. Do not output conversational text or <think> tags.
            Example: [{"sceneNumber": "1", "cameraAngle": "Wide", "visualDescription": "..."}]

            Task:
            1. Identify scenes/shots.
            2. For each, provide:
               - "sceneNumber": "Scene [X]"
               - "cameraAngle": "[Shot Type]"
               - "visualDescription": "A highly detailed visual prompt for AI image generation describing the subject, action, and lighting."
          `;
          
          const rawResponse = await generateLocal(
              `Analyze this script and output JSON:\n\n${fullScriptText.substring(0, 15000)}`, // Truncate for local context limits
              systemPrompt
          );

          // Cleaning up potential markdown or thinking tags from local models
          let cleanJson = rawResponse.replace(/<think>[\s\S]*?<\/think>/g, '').trim();
          cleanJson = cleanJson.replace(/```json/g, '').replace(/```/g, '').trim();

          const scenes = JSON.parse(cleanJson);
          if (!Array.isArray(scenes)) throw new Error("Local model returned invalid JSON structure.");
          
          onSpawnNodes(scenes, NodeType.TEXT_PROMPT); 

          onDataChange({ 
              ...data, 
              generationStatus: 'completed', 
              timestamp: Date.now(),
              cost: 0 // Local is free
          });

      } catch (e: any) {
          console.error(e);
          onDataChange({ ...data, generationStatus: 'error', errorMessage: "Local LLM Error: " + e.message });
      } finally {
          setIsProcessing(false);
          setIsModelDownloading(false);
      }
  };


  const handleBreakdown = async () => {
      if (isViewOnly) return;
      if (!hasFile || pages.length === 0) return;
      
      // Fork to Local Logic
      if (isLocalModel) {
          await handleLocalBreakdown();
          return;
      }

      const keyToUse = apiKey || process.env.API_KEY;
      if (!keyToUse) { setShowKeyError(true); return; }

      setIsProcessing(true);
      onDataChange({ ...data, generationStatus: 'generating', errorMessage: undefined });

      try {
          const fullScriptText = getFullText();
          const ai = new GoogleGenAI({ apiKey: keyToUse });
          
          const systemPrompt = `
            You are an expert film director and cinematographer. 
            Analyze the provided screenplay text.

            Instructions:
            1. Analyze the Script:
               - Read the provided text carefully.
               - Ignore formatting noise.
               - Identify the genre, mood, visual style, and key characters.
               - Maintain visual consistency.

            2. Break Down into Shots:
               - Divide the script into logical visual "beats."
               - Create a new shot whenever the camera angle needs to change, location changes, or significant action occurs.

            3. Format Your Output:
               Return ONLY a JSON array of objects. Each object must have these exact keys:
               - "sceneNumber": "Scene [X] - Shot [Y]"
               - "cameraAngle": "[Camera Angle/Shot Type]"
               - "visualDescription": "A highly detailed, optimized prompt for AI generation. Include: Subject (clothing, expression, pose), Action, Environment (setting, time of day), Cinematography (lens, lighting, angle), Style."
          `;

          const response = await ai.models.generateContent({
              model: currentModelId,
              contents: {
                  parts: [
                      { text: "Here is the screenplay content:\n\n" + fullScriptText },
                      { text: systemPrompt }
                  ]
              },
              config: { responseMimeType: 'application/json' }
          });

          const jsonText = response.text || "[]";
          const scenes = JSON.parse(jsonText);
          if (!Array.isArray(scenes)) throw new Error("Model did not return a valid list of scenes.");

          onSpawnNodes(scenes, NodeType.TEXT_PROMPT); 

          onDataChange({ 
              ...data, 
              generationStatus: 'completed', 
              timestamp: Date.now(),
              cost: AI_MODELS[currentModelId].baseCost 
          });

      } catch (e: any) {
          console.error(e);
          let errorMessage = e.message || "Failed to analyze script";
          if (errorMessage.includes('403') || errorMessage.includes('PERMISSION_DENIED')) {
             errorMessage = "Access Denied (403). Check API Key/Billing.";
          }
          onDataChange({ ...data, generationStatus: 'error', errorMessage });
      } finally {
          setIsProcessing(false);
      }
  };

  const handleExtractEntities = async (type: 'Character' | 'Prop' | 'Location') => {
      if (isViewOnly) return;
      if (!hasFile || pages.length === 0) return;
      
      // TODO: Implement local entity extraction similar to breakdown
      // For now, only cloud
      if (isLocalModel) {
          alert("Entity extraction currently supports Cloud models only.");
          return;
      }

      const keyToUse = apiKey || process.env.API_KEY;
      if (!keyToUse) { setShowKeyError(true); return; }

      setIsProcessing(true);
      onDataChange({ ...data, generationStatus: 'generating', errorMessage: undefined });

      try {
          const fullScriptText = getFullText();
          const ai = new GoogleGenAI({ apiKey: keyToUse });
          
          const systemPrompt = `
            Analyze the screenplay and extract all major ${type}s.
            Return a JSON array of objects with exactly these keys:
            - "name": "Name of the ${type}"
            - "description": "A concise visual description of the ${type} based on the script details (appearance, style, etc)."
            
            Ignore minor or background ${type}s.
          `;

          const response = await ai.models.generateContent({
              model: currentModelId,
              contents: {
                  parts: [
                      { text: fullScriptText },
                      { text: systemPrompt }
                  ]
              },
              config: { responseMimeType: 'application/json' }
          });

          const jsonText = response.text || "[]";
          const entities = JSON.parse(jsonText);
          
          let targetType = NodeType.CHARACTER_NODE;
          if (type === 'Prop') targetType = NodeType.PROP_NODE;
          if (type === 'Location') targetType = NodeType.LOCATION_NODE;

          onSpawnNodes(entities, targetType);

          onDataChange({ 
              ...data, 
              generationStatus: 'completed', 
              timestamp: Date.now(),
              cost: AI_MODELS[currentModelId].baseCost 
          });

      } catch (e: any) {
          onDataChange({ ...data, generationStatus: 'error', errorMessage: e.message });
      } finally {
          setIsProcessing(false);
      }
  };

  // The card's header says "Script". The row under it names the file, so it is there only while
  // there is one; with no file the empty state says what to do, once.
  return (
    <div className="w-full h-full flex flex-col bg-white">
        {/* The file: its name, its length, and the button that takes it out again */}
        {hasFile && (
            <div className="h-12 border-b border-cream-200 flex items-center px-3 gap-2.5 bg-cream-50 shrink-0">
                <span className="ns-mark ns-mark-sm">
                    <FileText />
                </span>
                <div className="flex-1 min-w-0">
                    <div className="ns-t-title truncate" title={fileName}>
                        {fileName || 'Screenplay'}
                    </div>
                    <div className="ns-t-meta flex items-center gap-2">
                        {isExtracting ? 'Extracting...' : `${pages.length} ${pages.length === 1 ? 'page' : 'pages'}`}
                        {status === 'completed' && <span className="ns-badge ns-badge-ok"><CheckCircle2 size={12} /> Analyzed</span>}
                    </div>
                </div>
                <button
                    onClick={() => onDataChange({ ...data, scriptConfig: undefined })}
                    className="ns-icon-btn ns-icon-btn-sm ns-icon-btn-danger"
                    title="Remove PDF"
                    aria-label="Remove PDF"
                >
                    <Trash2 />
                </button>
            </div>
        )}

        {/* Content area: the page on its desk, or the place a screenplay is dropped */}
        <div className={`flex-1 relative overflow-hidden flex flex-col ${hasFile ? 'bg-cream-200 p-2' : 'bg-cream-50'}`}>
            {/* Loading Overlay for Local Models */}
            {isModelDownloading && (
                <div className="ns-empty absolute inset-0 z-50 bg-white/95">
                    <span className="ns-mark">
                        <DownloadCloud />
                    </span>
                    <h3 className="ns-empty-title">Downloading model</h3>
                    <p className="ns-empty-text">{downloadProgress}</p>
                    <div className="ns-meter w-48 max-w-full mt-1.5" data-tone={nodeTone(NodeType.SCRIPT_PROCESSOR)}>
                        <span className="w-full animate-pulse" />
                    </div>
                </div>
            )}

            {!hasFile ? (
                <div
                    className={`ns-empty py-3 flex-1 relative transition-colors ${
                        dragActive ? 'bg-[color:var(--tone-soft)]' : 'hover:bg-cream-200'
                    }`}
                    onDragEnter={() => setDragActive(true)}
                    onDragLeave={() => setDragActive(false)}
                    onDrop={(e) => {
                        e.preventDefault();
                        setDragActive(false);
                        const file = e.dataTransfer.files?.[0];
                        if (file) processFile(file);
                    }}
                    onDragOver={e => e.preventDefault()}
                >
                    <input
                        type="file"
                        accept=".pdf"
                        onChange={handleFileChange}
                        className="absolute inset-0 opacity-0 cursor-pointer"
                        title="Choose a PDF screenplay"
                        aria-label="Choose a PDF screenplay"
                    />
                    <span className="ns-mark ns-mark-lg">
                        <Upload />
                    </span>
                    <span className="ns-empty-title">Add a screenplay</span>
                    <span className="ns-empty-text">Drop a PDF here, or click to choose one</span>
                </div>
            ) : (
                <div ref={containerRef} className="w-full h-full relative overflow-hidden">
                     {isExtracting ? (
                         <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                             <Loader2 className="animate-spin text-[color:var(--tone-deep)]" />
                             <span className="ns-t-small">Processing pages...</span>
                         </div>
                     ) : (
                         <div
                            className="bg-white shadow-soft absolute origin-top-left transition-transform duration-75 ease-out"
                            onPointerDown={(e) => e.stopPropagation()}
                            style={{
                                width: `${PAGE_WIDTH}px`,
                                height: `${PAGE_HEIGHT}px`,
                                transform: `translate(${viewState.x}px, ${viewState.y}px) scale(${viewState.scale})`
                            }}
                         >
                            {/* The page of the screenplay: Courier at 12px with lines 12px apart, which is what
                                the text taken from the PDF was laid out for (FALLBACK_CHAR_WIDTH, LINE_HEIGHT) */}
                            <div
                                className="w-full h-full px-12 py-10 font-mono text-xs text-ink select-text cursor-text flex flex-col items-start"
                                onDragStart={handleDragStart}
                                style={{
                                    fontFamily: '"Courier Prime", "Courier New", Courier, monospace',
                                    fontSize: '12px',
                                    lineHeight: '1',
                                }}
                            >
                                {currentLines.map((line, i) => {
                                    const { indent, text } = parseLine(line);
                                    if (!text && !indent) {
                                        return <div key={i} className="script-line w-full" style={{ height: '12px' }} data-indent=""></div>;
                                    }
                                    return (
                                        <div
                                            key={i}
                                            className="script-line flex w-full"
                                            data-indent={indent}
                                        >
                                            <div className="shrink-0 select-none pointer-events-none whitespace-pre">{indent}</div>
                                            <div className="text-content whitespace-pre">{text}</div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="absolute bottom-4 right-4 text-2xs text-ink-faint font-mono select-none pointer-events-none">
                                {currentPage}
                            </div>
                         </div>
                     )}
                </div>
            )}
        </div>

        {/* Footer */}
        <div className="shrink-0 border-t border-cream-200 bg-white flex flex-col">
            {/* The page, the model and the action. In a narrow node the row wraps, so the model's name is not cut */}
            <div className="min-h-[44px] flex flex-wrap items-center gap-x-2 gap-y-1.5 px-3 py-[7px]">
                {hasFile && !isExtracting && (
                    <div className="ns-chip shrink-0 gap-0.5 p-[3px]">
                        <button
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="ns-icon-btn ns-icon-btn-xs enabled:hover:bg-cream-300"
                            title="Previous page"
                            aria-label="Previous page"
                        >
                            <ChevronLeft />
                        </button>
                        <span className="tabular-nums min-w-[2.5rem] text-center">
                            {currentPage}/{pages.length}
                        </span>
                        <button
                            onClick={() => setCurrentPage(p => Math.min(pages.length, p + 1))}
                            disabled={currentPage === pages.length}
                            className="ns-icon-btn ns-icon-btn-xs enabled:hover:bg-cream-300"
                            title="Next page"
                            aria-label="Next page"
                        >
                            <ChevronRight />
                        </button>
                    </div>
                )}

                {/* The model: a chip that opens a list, as in the Image and Video nodes */}
                <SettingChip
                    icon={isLocalModel ? <Cpu size={14} /> : <Sparkles size={14} />}
                    label="Model"
                    value={currentModelId}
                    options={modelOptions}
                    onChange={(model) => updateConfig({ model })}
                    disabled={isProcessing || isViewOnly}
                    shrink
                />

                 {/* Main Action Button */}
                 {isLocalModel && !localModelReady ? (
                    <button
                        onClick={handleLoadModel}
                        disabled={isProcessing || isExtracting}
                        className="ns-btn ns-btn-primary ns-btn-xs whitespace-nowrap shrink-0 ml-auto"
                        title="Download the model to this browser (needs a GPU)"
                    >
                        {isProcessing ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                        Load model
                    </button>
                 ) : (
                    <button
                        onClick={handleBreakdown}
                        disabled={!hasFile || isProcessing || isExtracting}
                        className="ns-btn ns-btn-primary ns-btn-xs whitespace-nowrap shrink-0 ml-auto"
                    >
                        {isProcessing ? <Loader2 size={14} className="animate-spin" /> : <Play size={12} fill="currentColor" />}
                        {isProcessing ? 'Working...' : 'Breakdown'}
                    </button>
                 )}
            </div>

            {/* Entity Extraction Buttons: each in the colour and with the icon of the kind of node it makes */}
            {hasFile && (
                <div className="flex flex-wrap items-center justify-around gap-x-2 gap-y-1.5 px-3 py-[7px] border-t border-cream-200 bg-cream-50">
                    <button
                        disabled={isProcessing}
                        onClick={() => handleExtractEntities('Character')}
                        data-tone={nodeTone(NodeType.CHARACTER_NODE)}
                        className="ns-btn ns-btn-tone ns-btn-xs"
                        title="Make a Character node for each character in the script"
                    >
                        <NodeKindIcon type={NodeType.CHARACTER_NODE} size={14} /> Characters
                    </button>
                    <button
                        disabled={isProcessing}
                        onClick={() => handleExtractEntities('Prop')}
                        data-tone={nodeTone(NodeType.PROP_NODE)}
                        className="ns-btn ns-btn-tone ns-btn-xs"
                        title="Make a Prop node for each prop in the script"
                    >
                        <NodeKindIcon type={NodeType.PROP_NODE} size={14} /> Props
                    </button>
                    <button
                        disabled={isProcessing}
                        onClick={() => handleExtractEntities('Location')}
                        data-tone={nodeTone(NodeType.LOCATION_NODE)}
                        className="ns-btn ns-btn-tone ns-btn-xs"
                        title="Make a Location node for each location in the script"
                    >
                        <NodeKindIcon type={NodeType.LOCATION_NODE} size={14} /> Locations
                    </button>
                </div>
            )}
        </div>

        {showKeyError && (
             <div className="absolute inset-0 z-50 bg-white/95 flex flex-col items-center justify-center p-4 text-center">
                  <span className="ns-mark ns-mark-sm mb-2" data-tone="warn"><AlertCircle /></span>
                  <p className="ns-t-title mb-1">Google key required</p>
                  <p className="ns-t-small mb-2">You need a Gemini API key to analyze scripts in the cloud.</p>
                  <button onClick={() => setShowKeyError(false)} className="ns-btn ns-btn-soft ns-btn-xs">Dismiss</button>
             </div>
        )}
    </div>
  );
};
